import Utils from '../utils';

const { Communication, CommunicationStatus, Session } = Utils;

// Cache entries older than this are stale and evicted on read/write.
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000;
// Max carrier_services_* entries retained in localStorage TOTAL, across every
// customerId that has ever used this browser — not just the active user. A
// per-customer cap alone doesn't bound the browser's overall storage quota,
// since stale entries left behind by other customerIds on a shared/QA machine
// are never revisited otherwise.
const MAX_CACHE_ENTRIES = 30;
const CACHE_KEY_PREFIX = 'carrier_services_';

class CarrierServices {
	/**
	 * Generate cache key for carrier services based on customerId and ship date
	 * @param {string} shipDate
	 * @returns {string}
	 */
	static getCacheKey(shipDate) {
		const accountInfo = Session.getAccountInfo();
		const customerId = accountInfo?.customerId;

		return `carrier_services_${customerId}_${shipDate}`;
	}

	/**
	 * Get carrier services from cache if available and not expired
	 * @param {string} shipDate - Optional ship date parameter
	 * @returns {object|null} Cached data or null if not found/expired
	 */
	static getFromCache(shipDate) {
		const cacheKey = this.getCacheKey(shipDate);

		const cached = localStorage.getItem(cacheKey);
		if (!cached) {
			return null;
		}

		const { data, timestamp } = JSON.parse(cached);
		const now = Date.now();

		// Check if cache is still valid
		if (now - timestamp < CACHE_EXPIRY_MS) {
			return data;
		}

		localStorage.removeItem(cacheKey);
		return null;
	}

	/**
	 * Remove expired entries across ALL customerIds, then trim down to
	 * MAX_CACHE_ENTRIES - 1 oldest-first entries (again across all customerIds)
	 * to make room for a new write. Deliberately not scoped to the active
	 * user — see MAX_CACHE_ENTRIES comment. Unlike clearCache(), this
	 * preserves live multi-date cache entries.
	 */
	static pruneCache() {
		const now = Date.now();
		const liveEntries = [];

		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key || !key.startsWith(CACHE_KEY_PREFIX)) {
				continue;
			}

			let timestamp = 0;
			try {
				({ timestamp } = JSON.parse(localStorage.getItem(key)) || {});
			} catch {
				// Malformed entry — treat as oldest so it's evicted below.
			}

			if (now - timestamp >= CACHE_EXPIRY_MS) {
				localStorage.removeItem(key);
			} else {
				liveEntries.push({ key, timestamp });
			}
		}

		const overflow = liveEntries.length - (MAX_CACHE_ENTRIES - 1);
		if (overflow > 0) {
			liveEntries
				.sort((a, b) => a.timestamp - b.timestamp)
				.slice(0, overflow)
				.forEach(({ key }) => localStorage.removeItem(key));
		}
	}

	/**
	 * @param {unknown} error
	 * @returns {boolean}
	 */
	static isQuotaExceededError(error) {
		return (
			error instanceof DOMException &&
			(error.name === 'QuotaExceededError' || error.code === 22)
		);
	}

	/**
	 * Remove every carrier_services_* entry, across all customerIds. More
	 * aggressive than pruneCache() — used as a last resort when the cap alone
	 * didn't leave enough room to write (e.g. an unusually large payload, or
	 * quota consumed by other same-origin data).
	 */
	static removeAllEntries() {
		const keysToRemove = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && key.startsWith(CACHE_KEY_PREFIX)) {
				keysToRemove.push(key);
			}
		}
		keysToRemove.forEach(key => localStorage.removeItem(key));
	}

	/**
	 * Store carrier services in cache
	 * @param {string} shipDate - Optional ship date parameter
	 * @param {object} data - Data to cache
	 */
	static setCache(shipDate, data) {
		const cacheKey = this.getCacheKey(shipDate);

		// Bound cache growth without wiping other live shipDate entries.
		this.pruneCache();

		const payload = JSON.stringify({ data, timestamp: Date.now() });

		try {
			localStorage.setItem(cacheKey, payload);
		} catch (error) {
			if (!this.isQuotaExceededError(error)) {
				console.warn('CarrierServices: failed to write cache entry', error);
				return;
			}

			// pruneCache()'s cap didn't leave enough room — evict everything we
			// track and retry once before giving up.
			this.removeAllEntries();
			try {
				localStorage.setItem(cacheKey, payload);
			} catch (retryError) {
				// caching is best-effort; keep this observable in Sentry instead of
				// silently swallowing failures.
				console.warn(
					'CarrierServices: failed to write cache entry after evicting all cached entries',
					retryError
				);
			}
		}
	}

	/**
	 * Get carrier services - checks cache first, then makes API call if needed
	 * @param {string} shipDate
	 * @returns {Promise}
	 */
	static async getCarrierServices(shipDate) {
		const cachedData = this.getFromCache(shipDate);

		if (cachedData) {
			// Return cached data as a resolved promise to match API response format
			return Promise.resolve({
				status: CommunicationStatus.Success,
				data: cachedData,
			});
		}

		// Make API call if cache miss
		const response = await Communication.getCarrierServices(shipDate);

		if (Communication.isSuccessful(response) && response.data) {
			this.setCache(shipDate, response.data);
		}

		return response;
	}

	/**
	 * Clear carrier services cache for a specific user or all users
	 * @param {string} shipDate - Optional ship date to clear specific cache entry
	 * @param {number} userId - Optional user ID to clear specific user's cache
	 */
	static clearCache() {
		const accountInfo = Session.getAccountInfo();
		const customerId = accountInfo?.customerId;

		if (customerId) {
			const prefix = `carrier_services_${customerId}_`;
			const keysToRemove = [];
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				if (key && key.startsWith(prefix)) {
					keysToRemove.push(key);
				}
			}
			keysToRemove.forEach(key => {
				localStorage.removeItem(key);
			});
		}
	}
}
export default CarrierServices;
